import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '../types/database.generated'
import { isNativePlatform } from './platform'

let client: SupabaseClient<Database> | undefined

type SupabaseEnv = Pick<ImportMetaEnv, 'VITE_SUPABASE_URL' | 'VITE_SUPABASE_PUBLISHABLE_KEY' | 'VITE_SUPABASE_ANON_KEY'>

export function getSupabaseConfig(env: SupabaseEnv = import.meta.env) {
  return {
    url: env.VITE_SUPABASE_URL,
    // Supabase renamed legacy anon keys to publishable keys. Support both so
    // native and hosted production builds can use the same project values.
    key: env.VITE_SUPABASE_PUBLISHABLE_KEY ?? env.VITE_SUPABASE_ANON_KEY,
  }
}

export function getSupabaseClient(): SupabaseClient<Database> {
  const { url, key } = getSupabaseConfig()

  if (!url || !key) {
    throw new Error('Supabase is not configured. Copy .env.example to .env.local and add project credentials.')
  }

  client ??= createClient(url, key, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      // A Capacitor URL is the local WebView origin, not an OAuth callback URL.
      // Parsing it as one can repeatedly mutate/clean the WebView URL at startup.
      detectSessionInUrl: !isNativePlatform(),
    },
  })

  return client
}

export function isSupabaseConfigured(): boolean {
  const { url, key } = getSupabaseConfig()
  return Boolean(url && key)
}
